import { useRef, useEffect, useState } from "react";
import { useDisclosure } from "@helpers/useDisclosure";
import useResponsive from "@helpers/useResponsive";

type useActionsProps = {
  tableOfContents?: string[];
};

const useActions = ({ tableOfContents }: useActionsProps) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [isDisplayMenu, setIsDisplayMenu] = useState(false);
  const [offset, setOffset] = useState(0);

  const removeHtmlCode = (value: string): string => {
    const regex = /<\/?[^>]+(>|$)/g;
    return value.replace(regex, '');
  };

  const [selected, setSelected] = useState<string>(
    tableOfContents ? removeHtmlCode(tableOfContents[0]) : ''
  );

  const isMobile = useResponsive("md");
  const { isOpen, onOpen, onClose } = useDisclosure();

  const scrollToId = (id: string) => {
    const element = document.getElementById(id) as HTMLElement;
    const headerOffset = isMobile ? 60 + 80 : 70 + 40 + 64 + 32 + 32 + 4;
    const elementPosition = element?.getBoundingClientRect()?.top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

    if (elementPosition) {
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  const onClickMenuSwipeable = (item: string) => {
    setSelected(removeHtmlCode(item));
  };

  const handleScroll = () => {
    if (!menuRef.current) {
      return;
    }

    setIsDisplayMenu(window.scrollY > offset);

    const currentPosition = window.scrollY;

    (tableOfContents || []).forEach((section) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(section, 'text/html');
      const link = doc.querySelector('a.sidebar-link');
      const href = link?.getAttribute('href');
      const sectionId = href?.split('#')[1];

      const element = document.getElementById(sectionId || '');
      if (element) {
        const min = element?.offsetTop - 70 - 40 - 16;
        const max = element?.offsetTop + element?.offsetHeight - 70 - 40 - 16;
        if (min < currentPosition && max > currentPosition) {
          setSelected(removeHtmlCode(section));
        }
      }
    });
  };

  useEffect(() => {
    if (!menuRef.current) {
      return;
    }
    setOffset(menuRef.current.offsetTop + 70 + 40);
  }, [menuRef, setOffset]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [setIsDisplayMenu, menuRef, offset]);

  return {
    menuRef,
    isDisplayMenu,
    selected,
    isOpen,
    onOpen,
    onClose,
    scrollToId,
    onClickMenuSwipeable,
    isMobile,
    handleScroll,
    removeHtmlCode,
  };
};

export default useActions;
